export const LIMIT_COUNT = 5;
export const LIMIT_WINDOW_MS = 60 * 60 * 1000; // 10 minutes
const STORAGE_KEY = 'clara_ai_usage_timestamps';

export interface RateLimitResult {
  allowed: boolean;
  waitTimeMinutes?: number;
}

// Type guard for timestamps array
const isTimestampsArray = (data: unknown): data is number[] => {
  return Array.isArray(data) && data.every(item => typeof item === 'number');
};

const getTimestamps = (): number[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return isTimestampsArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const setTimestamps = (timestamps: number[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(timestamps));
  } catch (e) {
    console.warn('Failed to save rate limit timestamps:', e);
  }
};

export const checkRateLimit = (): RateLimitResult => {
  try {
    let timestamps = getTimestamps();
    const now = Date.now();

    // Filter out timestamps older than the window
    timestamps = timestamps.filter(t => now - t < LIMIT_WINDOW_MS);

    if (timestamps.length >= LIMIT_COUNT) {
      const oldest = timestamps[0];
      const waitMs = (oldest + LIMIT_WINDOW_MS) - now;
      const waitMinutes = Math.ceil(waitMs / 60000);
      return {
        allowed: false,
        waitTimeMinutes: waitMinutes > 0 ? waitMinutes : 1
      };
    }

    return { allowed: true };
  } catch (e) {
    console.error("Rate limit check failed", e);
    return { allowed: true }; // Fail open if storage error
  }
};

export const recordUsage = (): void => {
  try {
    let timestamps = getTimestamps();
    const now = Date.now();

    // Clean old
    timestamps = timestamps.filter(t => now - t < LIMIT_WINDOW_MS);
    // Add new
    timestamps.push(now);

    setTimestamps(timestamps);
  } catch (e) {
    console.error("Rate limit record failed", e);
  }
};

/**
 * Reset rate limit (useful for testing or admin purposes)
 */
export const resetRateLimit = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Get remaining usage count
 */
export const getRemainingUsage = (): number => {
  try {
    let timestamps = getTimestamps();
    const now = Date.now();
    timestamps = timestamps.filter(t => now - t < LIMIT_WINDOW_MS);
    return Math.max(0, LIMIT_COUNT - timestamps.length);
  } catch {
    return LIMIT_COUNT;
  }
};
